/**
 * MOVING A TICKET, IN THE ONE PLACE THAT KNOWS HOW.
 *
 * This component was defined inside `SupportTicketDesk.jsx` and used nowhere
 * else, because until script 360 nowhere else could use it: `fn_move_ticket`
 * asked for `view_support_tickets` and then for the stream grant, both Support
 * Centre permissions, and a Finance desk holding `modify_subscription_tier` was
 * refused with 42501 on every press.
 *
 * Script 360 gave the two act-on functions the rule `fn_tickets_on_my_desk` had
 * always enforced per row — *the person holds the permission this ticket's
 * section IS* — so the Finance tier desk can now work the ticket it has always
 * been able to read.
 *
 * EXTRACTED RATHER THAN COPIED, and that is the whole reason this file exists.
 * The legal transitions live in the database (`fn_ticket_move_is_allowed`,
 * thirteen pairs), are mirrored once in `MOVES`, and are checked against the
 * database by a test. A second copy of this control would be a second place for
 * the two screens to disagree about what a desk may do — and they would
 * disagree the first time somebody added a status to one of them.
 *
 * THE REFUSAL IS THE DATABASE'S, NEVER REWORDED HERE. *"A ticket cannot go
 * from resolved to triaged"* and *"this ticket is not on your desk"* are
 * sentences a migration chose; restating them in the browser is how a screen
 * starts telling somebody something the database does not think is true.
 */

import { useState } from 'react';
import { Field, Notice, PrimaryCTA, textInputStyle } from '../ui';
import { moveTicket, movesFrom, MOVE_WORDS } from '../lib/support';

/**
 * @param {object}   props
 * @param {object}   props.row     a ticket row: needs `id` and `status`
 * @param {Function} props.onDone  called after a move lands, to re-read
 * @param {string}   [props.hint]  what the note is for on THIS desk
 */
export default function TicketMove({ row, onDone, hint }) {
  const legal = movesFrom(row.status);
  const [to, setTo] = useState(legal[0] || '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  // A status with nowhere legal to go offers nothing. Rendering a disabled
  // select would be a control that says "you may not" where the truth is
  // "there is nothing left to do".
  if (legal.length === 0) return null;

  const go = async () => {
    setBusy(true);
    setFailure(null);
    const { error } = await moveTicket(row.id, to, note || null);
    setBusy(false);
    if (error) { setFailure(error); return; }
    onDone();
  };

  return (
    <div style={{ marginTop: 12 }}>
      <Field label="Move it to" htmlFor={`move-${row.id}`}>
        <select
          id={`move-${row.id}`}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          style={textInputStyle(false)}
        >
          {legal.map((s) => (
            <option key={s} value={s}>{MOVE_WORDS[s] || s}</option>
          ))}
        </select>
      </Field>
      <Field
        label="Note"
        hint={hint || 'Goes onto the trail against your name, and a resolution note is what the member reads.'}
        htmlFor={`note-${row.id}`}
      >
        <input
          id={`note-${row.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={textInputStyle(false)}
        />
      </Field>
      {failure && <Notice tone="critical">{failure}</Notice>}
      <PrimaryCTA onClick={go} busy={busy} disabled={busy}>
        {MOVE_WORDS[to] || 'Move it'}
      </PrimaryCTA>
    </div>
  );
}
